import { Truck, Music, Clock, Flame } from "lucide-react";
import { useSiteContent } from "@/hooks/useSiteContent";
import { resolveImageUrl } from "@/lib/api";

const FEATURES = [
  { icon: Flame, title: "Real Heat", text: "11 years of perfecting wings, burgers and 31 house sauces — fresh, every order." },
  { icon: Truck, title: "Two Locations", text: "Memphis on Lamar Ave and our newest spot in West Memphis, Arkansas." },
  { icon: Music, title: "Family Run", text: "A Cole-family operation — owners, chefs, cashiers and quality control." },
  { icon: Clock, title: "Order Easy", text: "DoorDash Smart Order for pickup or delivery." },
];

export const About = () => {
  const content = useSiteContent();
  const a = content?.about || {};
  const familyImg = resolveImageUrl(a.family_image_url || "/assets/team/cole-squad-restaurant.png");

  return (
  <section id="about" data-testid="about-section" className="bg-[#0A0A0A] py-20 md:py-28 border-t border-[#222]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Story */}
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start mb-20">
        <div className="lg:col-span-5">
          <div className="text-xs uppercase tracking-widest text-[#E60000] font-bold mb-3">
            {a.eyebrow || "Who We Are"}
          </div>
          <h2 className="font-heading text-5xl sm:text-6xl md:text-7xl text-white leading-none mb-6">
            {a.title_line1 || "Born In"} <br />
            <span className="text-[#E60000]">{a.title_accent || "Pine Hill"}</span>
          </h2>
          <p className="text-white/70 leading-relaxed text-base sm:text-lg mb-4">
            {a.story_paragraph_1 || "Hillboyz started 11 years ago in the Pine Hill neighborhood of South Memphis — where Loard and Tabitha Cole grew up. What began as a creative bodega-style stand on Gaither Street next to a car wash became a Memphis food-truck legend, feeding crews at Regional One, Memphis Shelby County Schools and regional warehouses for 8 years."}
          </p>
          <p className="text-white/70 leading-relaxed text-base sm:text-lg mb-4">
            {a.story_paragraph_2 || "In 2021 the demand was loud enough to open the first brick-and-mortar on Lamar Avenue. In the summer of 2025 the second location opened across the river in West Memphis, Arkansas."}
          </p>
          <blockquote className="border-l-2 border-[#E60000] pl-4 mt-6 italic text-white/85 text-base">
            "{a.quote || "No matter how large we grow, our guiding principle will always remain the same: if it costs more to do it right, it is worth it."}"
            <span className="block text-xs uppercase tracking-widest text-[#E60000] mt-2 not-italic font-bold">— {a.quote_author || "Loard Cole"}</span>
          </blockquote>
        </div>
        <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <div key={i} data-testid={`feature-${i}`} className="border border-[#333] hover:border-[#E60000] bg-[#1A1A1A] p-6 transition-colors">
                <div className="w-12 h-12 bg-[#E60000] flex items-center justify-center mb-4">
                  <Icon className="w-6 h-6 text-white" strokeWidth={2.5} />
                </div>
                <h3 className="font-heading text-2xl text-white mb-2">{f.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{f.text}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Family */}
      <div className="mb-10">
        <div className="text-xs uppercase tracking-widest text-[#E60000] font-bold mb-3">Meet The Family</div>
        <h3 className="font-heading text-4xl sm:text-5xl text-white">
          The <span className="text-[#E60000]">Cole Squad</span>
        </h3>
        <p className="text-white/60 max-w-2xl mt-3">
          Hillboyz is family-owned, family-staffed. Everyone has a job — from the chef to quality control.
        </p>
      </div>
      <div className="grid grid-cols-1 gap-5">
        <article
          data-testid="team-0"
          className="bg-[#1A1A1A] border border-[#333] hover:border-[#E60000] overflow-hidden group transition-colors grid md:grid-cols-5"
        >
          <div className="md:col-span-3 aspect-[4/3] md:aspect-auto overflow-hidden bg-[#0A0A0A]">
            <img
              src={familyImg}
              alt="The Whole Cole Squad"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              loading="lazy"
            />
          </div>
          <div className="md:col-span-2 p-6 md:p-8 flex flex-col justify-center">
            <h4 className="font-heading text-3xl md:text-4xl text-white mb-2">The Whole Cole Squad</h4>
            <div className="text-xs uppercase tracking-widest text-[#E60000] font-bold mb-4">Family-owned · Family-staffed</div>
            <p className="text-white/75 text-base leading-relaxed">
              {a.family_blurb || "Loard runs the kitchen, Tabitha co-owns and steers ops, Takiyah (17) is the chef-in-training learning every station, and Eva (16) holds it down at the register. Loard Jr (9) and Tyson (8) help out on the floor while big sister Lazaria cheers from college in Georgia. Everyone has a job. Everyone has a stake."}
            </p>
          </div>
        </article>
      </div>
    </div>
  </section>
  );
};
